import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { useMutation, useQueryClient } from 'react-query';

import { api } from '../services/api';
import { IBaseBatchResponse } from './types/BatchConsults';
import { apiLambda } from '../services/apiLambda';
import axios from 'axios';

const basePath = '/frotas';

type TUserRole =
  | 'ADMINISTRADOR'
  | 'ATENDIMENTO'
  | 'COORDENADOR'
  | 'MARKETING'
  | 'FINANCEIRO'
  | 'GERENCIAL'
  | 'COMERCIAL';

export interface IUserData {
  id?: number;
  sub?: string;
  name: string;
  email: string;
  phone: string;
  document?: string | null | undefined;
  status: 'ATIVO' | 'BLOQUEADO' | string;
  role: TUserRole | string;
  consult_balance: number;
  initial_consult_balance: number;
  isVerifiedEmail: boolean;
  created_at: Date;
  updated_at: Date;
  deleted_at: Date | null;
}

export interface IUpdateUserData {
  id: number;
  name?: string;
  email?: string;
  phone?: string;
  status?: 'ATIVO' | 'BLOQUEADO' | string;
  role?: TUserRole | string;
}

export interface ICreateUserData {
  password: string;
  name: string;
  email: string;
  phone: string;
  document: string | null | undefined;
  status: 'ATIVO' | 'BLOQUEADO' | string;
  role: TUserRole | string;
}

interface IPagedParams {
  resultsPerPage?: number;
  page?: number;
  column?: string;
  text?: string;
  role?: string;
  status?: string;
  orderBy?: string;
  orderByDirection: string;
}
export interface IGetUserPagedResult {
  results: IUserData[];
  nPages: number;
  total: number;
  resultsPerPage: string;
  currentPage: string;
}

export interface IAddUserContactToConsultsBatchRequest {
  cpf: string,
  cnpj: string,
  name: string,
  email: string,
  phone: string,
}


export async function listUsers(
  params: IPagedParams,
): Promise<IGetUserPagedResult> {
  const { data } = await api.get(`${basePath}/user-list-paginated`, {
    params,
  });

  const { nPages, results, resultsPerPage, currentPage, total } = data || [];

  return {
    results,
    nPages,
    total,
    resultsPerPage,
    currentPage,
  };
}

export async function createUser(dataObject: ICreateUserData): Promise<any> {
  const { data } = await api.post(`${basePath}/sign-up`, dataObject);
  return data;
}

export async function getUserSigned(): Promise<any> {
  const { data } = await api.get(`${basePath}/get-user-signed`);

  return data;
}

export async function getUserById(id: number): Promise<any> {
  const { data } = await api.get(`${basePath}/get-user/${id}`);

  return data;
}

export async function updateUser(dataObject: IUpdateUserData): Promise<any> {
  const { data } = await api.put(`${basePath}/update-user`, dataObject);
  return data;
}

export async function deleteUser(id: number): Promise<any> {
  const { data } = await api.delete(`${basePath}/delete-user/${id}`);

  return data;
}

export function useUsersList(params: IPagedParams) {
  params.orderByDirection = params.orderByDirection?.toUpperCase();
  const queryKey = useMemo(
    () => [
      'user_frotas',
      params.orderByDirection?.toUpperCase(),
      params.orderBy,
      params.column,
      params.text,
      params.role,
      params.status,
      params.page,
      params.resultsPerPage,
    ],
    [
      params.orderByDirection,
      params.orderBy,
      params.column,
      params.text,
      params.role,
      params.status,
      params.page,
      params.resultsPerPage,
    ],
  );

  return useQuery(queryKey, () => listUsers(params), {
    staleTime: 60000,
  });
}

async function resendEmail(userId: number): Promise<any> {
  const { data } = await api.post(`${basePath}/resend-email/${userId}`);
  return data;
}

export function useResendEmail() {
  const queryClient = useQueryClient();

  return useMutation(resendEmail, {
    onSuccess: () => {
      queryClient.invalidateQueries('user_frotas');
    },
  });
}

export async function addUserContactToConsultsBatch(
  batchCode: number,
  params: IAddUserContactToConsultsBatchRequest,
): Promise<IBaseBatchResponse> {
  const { data } = await apiLambda.patch(
    `/batch/add-contact/${batchCode}`,
    params,
  );
  return data;
}

export const getUserIP = async (): Promise<string> => {
  try {
    const response = await axios.get('https://api.ipify.org?format=json');
    return response.data.ip;
  } catch (error) {
    console.error("Erro ao obter o IP do usuário:", error);
    return '0.0.0.0'; 
  }
};
